import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import { useResponsive } from 'src/hooks/use-responsive';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import fr from 'date-fns/locale/fr';
import en from 'date-fns/locale/en-US';
import { addDays, parseISO, subDays, startOfMonth, startOfYear, subMonths, endOfMonth, differenceInDays, subYears, isValid, startOfDay, startOfWeek } from 'date-fns';
import { useEffect, useState, useRef } from 'react';
import { DateRangePicker, createStaticRanges, defaultInputRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { useLocales } from 'src/locales';
import { DateRangePickerProps } from './types';
import "./date-picker-custom.css";
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { enUS } from 'date-fns/locale';
import { getLast12WeeksDateRange } from 'src/utils/format-time';

// Aligns a date to the start of its granularity period (Monday for weekly, 1st for monthly)
const ALIGN_TIMESTEPS = new Set(['year_week', 'year_month']);

const alignDateToGranularity = (date: Date, timestep?: string): Date => {
  if (!timestep || !ALIGN_TIMESTEPS.has(timestep)) {
    return date;
  }

  if (timestep === 'year_month') {
    return startOfMonth(date);
  }

  // year_week: align to Monday
  return startOfWeek(date, { weekStartsOn: 1 });
};

export default function CustomDateRangePicker({
  variant = 'input',
  presets,
  onChangeStartDate,
  onChangeEndDate,
  onChangeCompareStartDate,
  onChangeCompareEndDate,
  error,
  enableComparison,
}: Partial<DateRangePickerProps & { enableComparison: boolean }>) {
  const mdUp = useResponsive('up', 'md');
  const filterStore = useSelector((state: any) => state.filters);
  const theme: any = useTheme();
  const { currentLang, t } = useLocales();
  const workspace = useCurrentWorkspace();

  const fallbackDate = startOfDay(new Date());
  
  // Track if initial comparison alignment has been applied (only once)
  const hasAppliedInitialComparisonAlignment = useRef(false);

  const [state, setState] = useState(() => ({
    selection1: {
      startDate: isValid(parseISO(filterStore?.start)) ? parseISO(filterStore?.start) : fallbackDate,
      endDate: isValid(parseISO(filterStore?.end)) ? parseISO(filterStore?.end) : fallbackDate,
      key: 'selection1',
    },
    selection2: {
      startDate: isValid(parseISO(filterStore?.compareStart)) ? parseISO(filterStore?.compareStart) : fallbackDate,
      endDate: isValid(parseISO(filterStore?.compareEnd)) ? parseISO(filterStore?.compareEnd) : fallbackDate,
      key: 'selection2',
    },
  }));

  useEffect(() => {
    // Only apply initial alignment ONCE when comparison is first enabled
    if (enableComparison && !hasAppliedInitialComparisonAlignment.current) {
      hasAppliedInitialComparisonAlignment.current = true;
      
      const { startDate, endDate } = state.selection1;
      const timestep = filterStore?.timestep;
      
      // Calculate the duration of the main period
      const diffDays = differenceInDays(endDate, startDate);
      
      // Calculate raw comparison start date (just before the main period)
      const rawCompareStart = subDays(startDate, diffDays + 1);
      
      // Align the comparison start date according to granularity
      const alignedCompareStart = alignDateToGranularity(rawCompareStart, timestep);
      
      // Calculate end date so comparison period has the same duration
      const alignedCompareEnd = addDays(alignedCompareStart, diffDays);

      setState(prevState => ({
        ...prevState,
        selection2: {
          startDate: alignedCompareStart,
          endDate: alignedCompareEnd,
          key: 'selection2'
        }
      }));
    }
    
    // Reset the flag when comparison is disabled so next enable will re-align
    if (!enableComparison) {
      hasAppliedInitialComparisonAlignment.current = false;
    }
  }, [enableComparison]);

  useEffect(() => {
    setState({
      selection1: {
        startDate: parseISO(filterStore?.start),
        endDate: parseISO(filterStore?.end),
        key: 'selection1'
      },
      selection2: {
        startDate: parseISO(filterStore?.compareStart),
        endDate: parseISO(filterStore?.compareEnd),
        key: 'selection2'
      },
    });
  }, [filterStore]);

  useEffect(() => {
    if (onChangeStartDate) {
      onChangeStartDate(state.selection1.startDate);
    }

    if (onChangeEndDate) {
      onChangeEndDate(state.selection1.endDate);
    }
  }, [state.selection1, onChangeStartDate, onChangeEndDate]);

  useEffect(() => {
    if (onChangeCompareStartDate) {
      onChangeCompareStartDate(state.selection2.startDate);
    }

    if (onChangeCompareEndDate) {
      onChangeCompareEndDate(state.selection2.endDate);
    }
  }, [state.selection2, onChangeCompareStartDate, onChangeCompareEndDate]);

  useEffect(() => {
    if (theme.palette.mode === "dark") {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }, [theme.palette.mode]);

  const isCalendarView = variant === 'calendar';

  const customStaticRanges = createStaticRanges([
    {
      label: t('dashboard.filters.this_month'),
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_month'),
      range: () => ({
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1)),
      }),
    },
    {
      label: t('dashboard.filters.last_7_days'),
      range: () => ({
        startDate: subDays(new Date(), 7),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_14_days'),
      range: () => ({
        startDate: subDays(new Date(), 14),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_30_days'),
      range: () => ({
        startDate: subDays(new Date(), 30),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_90_days'),
      range: () => ({
        startDate: subDays(new Date(), 90),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_12_weeks'),
      range: () => {
        const { start, end } = getLast12WeeksDateRange();
        return {
          startDate: new Date(start),
          endDate: new Date(end),
        };
      },
    },
    {
      label: t('dashboard.filters.this_year'),
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: subDays(new Date(), 1),
      }),
    },
    ...(enableComparison ? [
      {
        label: '‎ ‎ ‎ ‎ ',
        range: () => {

          return {

          };
        },
      },
      {
        label: t('dashboard.filters.previous_period'),
        range: () => {
          const { startDate, endDate } = state.selection1;
          const timestep = filterStore?.timestep;
          
          // Calculate the duration of the main period
          const diffDays = differenceInDays(endDate, startDate);
          
          // Calculate raw comparison start date (just before the main period)
          const rawCompareStart = subDays(startDate, diffDays + 1);
          
          // Align the comparison start date according to granularity
          const alignedCompareStart = alignDateToGranularity(rawCompareStart, timestep);
          
          // Calculate end date so comparison period has the same duration
          const alignedCompareEnd = addDays(alignedCompareStart, diffDays);
          
          return {
            startDate: alignedCompareStart,
            endDate: alignedCompareEnd,
          };
        },
      },
      {
        label: t('dashboard.filters.last_year'),
        range: () => {
          const { startDate, endDate } = state.selection1;
          return {
            startDate: subYears(startDate, 1),
            endDate: subYears(endDate, 1),
          };
        },
      }
    ] : []),
  ]);


  const customInputRanges = defaultInputRanges;

  const handleRangeChange = (item: any) => {
    if (item.selection1) {
      const lastYearRange: any = customStaticRanges.find(
        (range) => range.label === t('dashboard.filters.last_year')
      );
      const previousPeriodRange: any = customStaticRanges.find(
        (range) => range.label === t('dashboard.filters.previous_period')
      );

      const newStart = item.selection1.startDate;
      const newEnd = item.selection1.endDate;

      // 🧠 Cas 1 : Last Year preset
      if (
        lastYearRange &&
        newStart.getTime() === lastYearRange.range().startDate.getTime() &&
        newEnd.getTime() === lastYearRange.range().endDate.getTime()
      ) {
        setState((prev) => ({
          ...prev,
          selection2: {
            startDate: subYears(prev.selection1.startDate, 1),
            endDate: subYears(prev.selection1.endDate, 1),
            key: 'selection2',
          },
        }));
        
        return;
      }

      // 🧠 Cas 2 : Previous period preset
      if (
        previousPeriodRange &&
        newStart.getTime() === previousPeriodRange.range().startDate.getTime() &&
        newEnd.getTime() === previousPeriodRange.range().endDate.getTime()
      ) {
        const diffDays = differenceInDays(newEnd, newStart) + 1;
        setState((prev) => ({
          ...prev,
          selection2: {
            startDate: subDays(prev.selection1.startDate, diffDays),
            endDate: subDays(prev.selection1.startDate, 1),
            key: 'selection2',
          },
        }));
        return;
      }

      // 🧠 Cas 3 : Saisie manuelle
      setState((prev) => {
        // l’utilisateur modifie la start (end devient identique => typique d’un input manuel)
        if (
          newStart &&
          newStart.getTime() !== prev.selection1.startDate.getTime() &&
          newEnd.getTime() === newStart.getTime()
        ) {
          return {
            ...prev,
            selection1: {
              ...prev.selection1,
              startDate: newStart,
            },
          };
        }

        // l’utilisateur modifie la end (start devient identique => idem)
        if (
          newEnd.getTime() !== prev.selection1.endDate.getTime() &&
          newStart.getTime() === prev.selection1.startDate.getTime()
        ) {
          return {
            ...prev,
            selection1: {
              ...prev.selection1,
              endDate: newEnd,
            },
          };
        }

        // 🧠 Cas normal : clic sur le calendrier
        return {
          ...prev,
          ...item,
        };
      });
    } else if (item.selection2) {
      const newStart = item.selection2.startDate;
      const newEnd = item.selection2.endDate;

      setState((prev) => {
        if (
          newStart &&
          newStart.getTime() !== prev.selection2.startDate.getTime() &&
          newEnd.getTime() === newStart.getTime()
        ) {
          return {
            ...prev,
            selection2: {
              ...prev.selection2,
              startDate: newStart,
            },
          };
        }

        if (
          newEnd.getTime() !== prev.selection2.endDate.getTime() &&
          newStart.getTime() === prev.selection2.startDate.getTime()
        ) {
          return {
            ...prev,
            selection2: {
              ...prev.selection2,
              endDate: newEnd,
            },
          };
        }

        return {
          ...prev,
          ...item,
        };
      });
    }
  }

    const customEnglishLocale: any = {
      ...enUS,
      options: {
        ...enUS.options,
        weekStartsOn: 1, // 1 = lundi
      },
    };

    return (
      <>
        <Stack
          justifyContent="center"
          spacing={isCalendarView ? (presets ? 2 : 3) : 2}
          direction={isCalendarView && mdUp ? 'row' : 'column'}
          sx={{ pt: 1 }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              borderStyle: 'dashed',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLang?.value === "en" ? en : fr}>
              <DateRangePicker
                onChange={handleRangeChange}
                ranges={enableComparison ? [state.selection1, state.selection2] : [state.selection1]}
                minDate={
                  isValid(new Date(filterStore?.minDatePicker))
                    ? new Date(filterStore.minDatePicker)
                    : subYears(new Date(), 1)
                }
                maxDate={workspace?.url === "client" ? new Date(2024, 10, 1) : addDays(new Date(), -1)}
                locale={currentLang.value === 'en' ? customEnglishLocale : fr}
                direction="vertical"
                scroll={{ enabled: true }}
                editableDateInputs={true}
                staticRanges={customStaticRanges}
                inputRanges={customInputRanges}
                dateDisplayFormat={currentLang?.value === "en" ? "MM/dd/yyyy" : 'dd/MM/yyyy'}

              />
            </LocalizationProvider>
          </Paper>
        </Stack>

        {error && (
          <FormHelperText error sx={{ px: 2 }}>
            {t('dashboard.filters.end_date_must_be_later_than_start_date')}
          </FormHelperText>
        )}
      </>
    );
  }
